// prettier-ignore
class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
    this.allPredictiveSearchInstances = document.querySelectorAll("predictive-search");
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = "";

    // UI único (tu card)
    this.body = document.querySelector("body");
    this.header = document.querySelector(".shopify-section-header");
    this.headerBottom = document.querySelector(".header__bottom");
    this.promoModal = document.querySelector(".search__modal");
    this.promoHost  = document.querySelector(".search__modal .search__items");
    this.promoOriginalHTML = this.promoHost ? this.promoHost.innerHTML : "";
    this.collectionList = document.querySelector(".template-404__collections");
    this.headerInput = document.querySelector(".header__search .search__input");

    this.calcHeight();
    this.setupEventListeners();

    this.syncPanelSize();
    window.addEventListener("resize", this.syncPanelSize.bind(this));
    window.addEventListener("orientationchange", this.syncPanelSize.bind(this));

    if (this.predictiveSearchResults) {
      new MutationObserver(() => {
        this.syncPanelSize();
        this.setActiveListSizing();
      }).observe(this.predictiveSearchResults, { childList: true, subtree: true });
    }
  }

  /* ---------- DIMENSIONES ---------- */
  calcHeight() {
    const root = this.promoModal || document;
    this.results = root.querySelector(".predictive-search__result-group");
    this.resultsTabs = root.querySelector(".predictive-search__result-tabs");
    if (this.results && this.resultsTabs) {
      this.results.style.setProperty(
        "--search-height",
        `${parseInt(this.resultsTabs.getBoundingClientRect().bottom)}px`
      );
    }
  }

  syncPanelSize() {
    const field = this.input?.form || this;
    if (!field) return;
    const w = Math.round(field.getBoundingClientRect().width || 0);
    const clamped = Math.max(320, Math.min(w, 1200));
    document.documentElement.style.setProperty("--ps-panel-max", clamped + "px");
  }

  setActiveListSizing() {
    const root = this.promoModal || document;
    root.querySelectorAll(".predictive-search__results-list").forEach(l => l.classList.remove("is-single"));
    const active = root.querySelector(".predictive-search__results-list.active") || root.querySelector(".predictive-search__results-list");
    if (!active) return;
    const count = active.querySelectorAll("li, .predictive-search__list-item, .predictive-search__item").length;
    if (count === 1) active.classList.add("is-single");
  }

  /* ---------- EVENTOS ---------- */
  setupEventListeners() {
    if (this.input?.form) this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));
    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
  }

  getQuery() { return this.input.value.trim(); }

  onChange() {
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;
    if (!this.searchTerm.length) { this.close(true); return; }
    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length ||
        this.querySelector('[aria-selected="true"] a') ||
        event.submitter.classList.contains("card__link")) {
      event.preventDefault();
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = "";
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();
    if (this.classList.contains("search-modal__form")) {
      if (this.headerBottom) this.headerBottom.classList.add("header__bottom--visible");
      if (this.promoModal)  this.promoModal.classList.remove("search__modal--hidden");
      if (this.collectionList) this.collectionList.classList.remove("hidden");
    }
    this.syncPanelSize();
    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute("results") === "true") {
      this.open();
      this.setActiveListSizing();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    if (this.classList.contains("search-modal__form")) {
      if (this.headerBottom) this.headerBottom.classList.remove("header__bottom--visible");
    }
    if (!this.classList.contains("search-modal__form")) {
      setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close(true);
      });
    }
  }

  onBlur() { this.headerInput?.blur(); }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();
    switch (event.code) {
      case "Enter":
        event.preventDefault();
        this.selectOption();
        break;
      case "Escape":
        this.close(true);
        this.closeResults(true);
        break;
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const el = this.querySelector("[data-predictive-search-search-for-text]") || null;
    const currentButtonText = el?.innerText;
    if (currentButtonText && previousTerm) {
      const matchesLen = [...currentButtonText.matchAll(new RegExp(previousTerm, "g"))].length;
      if (matchesLen > 1) return;
      el.innerText = currentButtonText.replace(previousTerm, newTerm);
    }
  }

  selectOption() {
    const root = this.promoModal || this;
    const selectedOption =
      root.querySelector('[aria-selected="true"] a, button[aria-selected="true"]') ||
      this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
    if (selectedOption) selectedOption.click();
  }

  /* ---------- BUSCAR ---------- */
  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    if (this.collectionList) this.collectionList.classList.add("hidden");

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`,
      { signal: this.abortController.signal }
    )
      .then((response) => {
        if (!response.ok) { var error = new Error(response.status); this.close(); throw error; }
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;

        this.allPredictiveSearchInstances.forEach((inst) => {
          inst.cachedResults[queryKey] = resultsMarkup;
        });

        this.renderSearchResults(resultsMarkup);
        try { colorSwatches?.(); } catch (err) {}
      })
      .catch((error) => {
        if (error?.code === 20) return;
        this.close();
        throw error;
      });
  }

  /* ---------- PINTAR EN TU CARD (sin duplicados, tabs visibles, imágenes cargadas) ---------- */
  renderSearchResults(resultsMarkup) {
    // Oculta el contenedor nativo para evitar el segundo panel
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.innerHTML = "";
      this.predictiveSearchResults.style.display = "none";
      this.predictiveSearchResults.setAttribute("aria-hidden","true");
    }

    const doc = new DOMParser().parseFromString(resultsMarkup, "text/html");
    this.stripQuerySuggestions(doc);

    // Toma la PRIMERA barra de tabs que exista y todo el grupo de resultados
    const tabs  = doc.querySelector(".predictive-search__result-tabs");
    const group = doc.querySelector(".predictive-search__result-group");

    // Construye el HTML final
    const frag = document.createDocumentFragment();
    if (tabs)  frag.appendChild(tabs.cloneNode(true));
    if (group) frag.appendChild(group.cloneNode(true));

    if (this.promoHost) {
      this.promoHost.replaceChildren(frag);

      // Quita duplicados (si vinieran tabs/listas repetidas)
      this.dedupeTabsAndLists();

      // Muestra SOLO la categoría activa (products por defecto si existe)
      const defaultType = this.getActiveType()
                        || (this.promoHost.querySelector('[data-type="products"]') ? "products" : this.firstListType());
      this.showOnlyType(defaultType);

      // Forzar carga de miniaturas
      this.forceLoadImages(this.promoHost);

      this.promoModal?.classList.add("search__modal--results");
      this.promoModal?.classList.remove("search__modal--home");
    }

    this.setAttribute("results", true);
    this.setLiveRegionResultsFromMarkup(doc);
    this.open();
    this.syncPanelSize();
    this.setActiveListSizing();
    this.bindTabs(); // tabs funcionales
  }

  /* ——— Tabs / Listas helpers ——— */
  dedupeTabsAndLists(){
    if (!this.promoHost) return;
    // Tabs: deja sólo la primera
    const tabs = this.promoHost.querySelectorAll(".predictive-search__result-tabs");
    tabs.forEach((el, i) => { if (i > 0) el.remove(); });
    // Listas duplicadas por tipo → deja la primera
    const seen = {};
    this.promoHost.querySelectorAll(".predictive-search__results-list").forEach(list=>{
      const t = list.dataset.type || "__unknown__";
      if (seen[t]) list.remove(); else seen[t] = list;
    });
  }

  getActiveType(){
    const root = this.promoModal || document;
    const activeTab = root.querySelector(".predictive-search__result-tab.active");
    if (activeTab?.dataset?.typeTarget) return activeTab.dataset.typeTarget;
    const activeList = root.querySelector(".predictive-search__results-list.active");
    if (activeList?.dataset?.type) return activeList.dataset.type;
    return null;
  }

  firstListType(){
    const first = (this.promoModal || document).querySelector(".predictive-search__results-list");
    return first?.dataset?.type || null;
  }

  showOnlyType(type){
    const root  = this.promoModal || document;
    const tabs  = root.querySelectorAll(".predictive-search__result-tab");
    const lists = root.querySelectorAll(".predictive-search__results-list");

    tabs.forEach(t => t.classList.toggle("active", t.dataset.typeTarget === type));

    lists.forEach(list => {
      const match = list.dataset.type === type;
      list.classList.toggle("active", match);
      list.style.display = match ? "grid" : "none"; // fuerza visibilidad correcta
    });

    this.setActiveListSizing();
    this.syncPanelSize();
  }

  bindTabs() {
    const root = this.promoModal || document;
    root.querySelectorAll(".predictive-search__result-tab").forEach((tab) => {
      tab.addEventListener("click", (event) => {
        event.preventDefault();
        const typeTarget = tab.dataset.typeTarget;
        this.showOnlyType(typeTarget);
        this.forceLoadImages(root);
      }, { once:false });
    });
  }

  /* —— Limpieza de queries y wrappers vacíos (FIX: usa AND, no OR) —— */
  stripQuerySuggestions(doc){
    const killSelectors = [
      '.predictive-search__search-for',
      '[data-predictive-search-search-for]',
      '.predictive-search__item--term',
      '.predictive-search__item--query',
      '.predictive-search__link--term',
      '#predictive-search-results-queries',
      '[id*="predictive-search-results-queries"]',
      '.predictive-search__results-list--queries',
      '[data-type="queries"]',
      '[data-predictive-search-queries]',
      'form[role="search"]',
      '.search-modal__form'
    ];
    doc.querySelectorAll(killSelectors.join(',')).forEach(n => n.remove());

    // ⚠️ Solo elimina si NO tiene hijos Y no tiene texto (AND)
    doc.querySelectorAll('.predictive-search__results-list, .predictive-search__results, .predictive-search__result-group, ul, ol, div')
      .forEach(el => {
        if (!el.children.length && !el.textContent.trim()) el.remove();
      });
  }

  /* Forzar carga de imágenes/miniaturas (lazy) */
  forceLoadImages(root){
    if (!root) return;
    root.querySelectorAll('img[data-src], img[data-srcset], source[data-srcset]').forEach(el=>{
      if (el.dataset.src)    { el.src = el.dataset.src; el.removeAttribute('data-src'); }
      if (el.dataset.srcset) { el.srcset = el.dataset.srcset; el.removeAttribute('data-srcset'); }
      if (el.tagName === 'IMG') el.loading = "eager";
    });
    if (window.lazySizes?.loader) {
      root.querySelectorAll('.lazyload, .lazyloading').forEach(el => lazySizes.loader.unveil(el));
    }
  }

  /* ---------- ARIA ---------- */
  setLiveRegionResultsFromMarkup(doc) {
    this.removeAttribute("loading");
    const countEl = doc.querySelector("[data-predictive-search-live-region-count-value]");
    if (countEl) this.setLiveRegionText(countEl.textContent);
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText = this.loadingText || this.getAttribute("data-loading-text");
    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;
    setTimeout(() => { this.statusElement.setAttribute("aria-hidden", "true"); }, 1000);
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    const countEl = this.querySelector("[data-predictive-search-live-region-count-value]");
    if (countEl) this.setLiveRegionText(countEl.textContent);
  }

  /* ---------- ABRIR / CERRAR ---------- */
  open() {
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }
    const selected = (this.promoModal || document).querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("loading");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);

    this.showPromoBlock();
  }

  /* ---------- PROMO ---------- */
  hidePromoBlock() { if (this.collectionList) this.collectionList.classList.add("hidden"); }

  showPromoBlock() {
    if (this.promoHost && this.promoOriginalHTML) {
      this.promoHost.innerHTML = this.promoOriginalHTML;
      this.promoModal?.classList.remove("search__modal--results");
      this.promoModal?.classList.add("search__modal--home");
    }
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.style.display = "";
      this.predictiveSearchResults.removeAttribute("aria-hidden");
    }
    if (this.collectionList) this.collectionList.classList.remove("hidden");
  }
}

customElements.define("predictive-search", PredictiveSearch);
